import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  ArrowUpRight,
  MessageCircle,
  Clock,
  Globe,
  CheckCircle2,
  Sparkles,
  Loader2,
} from "lucide-react";
import axios from "axios";

export default function ContactPage({
  accent = "#115e32",
  pageTitle = "Contact Us | University of Creative Technology Chittagong",
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    subject: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        ...formData,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/contact-form`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setSuccess(
          response.data?.message || "Your message has been sent successfully!"
        );

        setFormData({
          name: "",
          email: "",
          message: "",
          subject: "",
        });
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Unable to send your message. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    {
      icon: Mail,
      title: "Email Us",
      detail: "info@uctc.edu.bd",
      description: "Send us your questions anytime.",
      link: "mailto:info@uctc.edu.bd",
      color: "from-cyan-500 to-blue-600",
      light: "bg-cyan-50",
    },
    {
      icon: Phone,
      title: "Call Us",
      detail: "+8801707508080",
      secondary: "+8801707508081",
      description: "We are happy to assist you.",
      link: "tel:+8801707508080",
      color: "from-emerald-500 to-teal-600",
      light: "bg-emerald-50",
    },
    {
      icon: MapPin,
      title: "Visit Our Campus",
      detail: "Chandgaon, Chattogram",
      description: "1084, Shah Amanat Bridge Connecting Road.",
      link: "https://maps.google.com/?q=University+of+Creative+Technology+Chittagong",
      color: "from-violet-500 to-indigo-600",
      light: "bg-violet-50",
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="overflow-hidden bg-slate-50 text-slate-800">
      {/* ================= HERO SECTION ================= */}
      <section className="relative isolate overflow-hidden bg-gradient-to-br from-[#063b55] via-[#075b75] to-[#087f8c]">
        {/* Decorative backgrounds */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <motion.div
            animate={{
              x: [0, 30, 0],
              y: [0, -25, 0],
              scale: [1, 1.08, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -right-32 -top-40 h-[500px] w-[500px] rounded-full bg-cyan-300/10 blur-3xl"
          />

          <motion.div
            animate={{
              x: [0, -25, 0],
              y: [0, 25, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-48 -left-32 h-[450px] w-[450px] rounded-full bg-emerald-300/10 blur-3xl"
          />

          <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.5)_1px,transparent_1px)] [background-size:55px_55px]" />
        </div>

        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 sm:px-8 md:py-28 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.15,
                },
              },
            }}
            className="relative z-10 text-center lg:text-left"
          >
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-cyan-100 backdrop-blur-md"
            >
              <Sparkles size={16} />
              We’re here to help
            </motion.div>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="text-4xl font-black leading-tight tracking-tight text-white sm:text-5xl md:text-6xl"
            >
              Let’s Start a
              <span className="block bg-gradient-to-r from-cyan-200 to-emerald-200 bg-clip-text text-transparent">
                Conversation
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mx-auto mt-6 max-w-xl text-base leading-8 text-cyan-50/80 sm:text-lg lg:mx-0"
            >
              Have a question about admissions, academics, or university
              services? Our team is ready to connect with you.
            </motion.p>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mt-9 flex flex-wrap justify-center gap-4 lg:justify-start"
            >
              <a
                href="#contact-form"
                className="group inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 font-bold text-[#075b75] shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
              >
                Send a Message
                <ArrowUpRight
                  size={18}
                  className="transition group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </a>

              <a
                href="tel:+8801707508080"
                className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-6 py-3.5 font-semibold text-white backdrop-blur-md transition hover:bg-white/20"
              >
                <Phone size={18} />
                Call Us
              </a>
            </motion.div>
          </motion.div>

          {/* Hero visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="relative mx-auto w-full max-w-lg"
          >
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="relative rounded-[2rem] border border-white/20 bg-white/10 p-5 shadow-2xl backdrop-blur-xl sm:p-7"
            >
              <div className="rounded-[1.5rem] bg-white p-6 sm:p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-400">
                      UNIVERSITY SUPPORT
                    </p>
                    <h2 className="mt-2 text-2xl font-extrabold text-slate-800">
                      How can we help?
                    </h2>
                  </div>

                  <div className="rounded-2xl bg-cyan-50 p-3 text-cyan-700">
                    <MessageCircle size={28} />
                  </div>
                </div>

                <div className="mt-7 space-y-4">
                  {[
                    "Admission information",
                    "Academic support",
                    "Campus services",
                  ].map((item, index) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.4 + index * 0.15,
                        duration: 0.5,
                      }}
                      className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4"
                    >
                      <div className="rounded-lg bg-emerald-100 p-2 text-emerald-700">
                        <CheckCircle2 size={18} />
                      </div>
                      <span className="font-semibold text-slate-700">
                        {item}
                      </span>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-6 flex items-center gap-3 rounded-xl bg-gradient-to-r from-[#075b75] to-[#087f8c] p-4 text-white">
                  <div className="rounded-lg bg-white/15 p-2">
                    <Clock size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-bold">We’re ready to listen</p>
                    <p className="text-xs text-white/75">
                      Reach out and let us know.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{ rotate: [0, 8, 0], y: [0, -8, 0] }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -right-4 -top-5 rounded-2xl border border-white/20 bg-white/15 p-4 text-white shadow-xl backdrop-blur-lg sm:-right-7"
            >
              <Mail size={24} />
            </motion.div>

            <motion.div
              animate={{ rotate: [0, -8, 0], y: [0, 8, 0] }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -bottom-5 -left-4 rounded-2xl border border-white/20 bg-white/15 p-4 text-white shadow-xl backdrop-blur-lg sm:-left-7"
            >
              <Phone size={24} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= CONTACT CARDS ================= */}
      <section className="relative z-10 -mt-8 px-6 sm:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.15,
              },
            },
          }}
          className="mx-auto grid max-w-7xl gap-5 md:grid-cols-3"
        >
          {contactItems.map((item) => {
            const Icon = item.icon;

            return (
              <motion.a
                key={item.title}
                href={item.link}
                target={item.title === "Visit Our Campus" ? "_blank" : undefined}
                rel={
                  item.title === "Visit Our Campus"
                    ? "noopener noreferrer"
                    : undefined
                }
                variants={fadeUp}
                transition={{ duration: 0.6 }}
                whileHover={{ y: -8 }}
                className="group rounded-2xl border border-slate-100 bg-white p-6 shadow-lg shadow-slate-200/50 transition hover:shadow-2xl sm:p-7"
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`rounded-2xl bg-gradient-to-br ${item.color} p-3.5 text-white shadow-lg`}
                  >
                    <Icon size={24} />
                  </div>

                  <ArrowUpRight
                    size={20}
                    className="text-slate-300 transition group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-cyan-700"
                  />
                </div>

                <h3 className="mt-5 text-lg font-extrabold text-slate-800">
                  {item.title}
                </h3>

                <p className="mt-2 break-words font-bold text-[#075b75]">
                  {item.detail}
                </p>

                {item.secondary && (
                  <p className="mt-1 font-semibold text-slate-600">
                    {item.secondary}
                  </p>
                )}

                <p className="mt-3 text-sm leading-6 text-slate-500">
                  {item.description}
                </p>
              </motion.a>
            );
          })}
        </motion.div>
      </section>

      {/* ================= CONTACT FORM ================= */}
      <section
        id="contact-form"
        className="relative overflow-hidden px-6 py-20 sm:px-8 md:py-28"
      >
        <div className="absolute -left-40 top-20 h-96 w-96 rounded-full bg-cyan-100/60 blur-3xl" />
        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-emerald-100/60 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-start gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          {/* Form introduction */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="pt-4"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-cyan-100 px-4 py-2 text-sm font-bold text-[#075b75]">
              <MessageCircle size={16} />
              Contact our team
            </div>

            <h2 className="mt-6 text-3xl font-black leading-tight text-slate-900 sm:text-4xl md:text-5xl">
              Send Us a
              <span className="block text-[#087f8c]">Message</span>
            </h2>

            <p className="mt-5 max-w-lg text-base leading-8 text-slate-600">
              Fill out the form and share your inquiry with us. We look forward
              to hearing from you.
            </p>

            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4">
                <div className="rounded-xl bg-white p-3 text-[#087f8c] shadow-md">
                  <Globe size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">
                    University of Creative Technology Chittagong
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Chandgaon Police Station, 1084, Shah Amanat Bridge
                    Connecting Road, Chattogram 4212
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="rounded-xl bg-white p-3 text-[#087f8c] shadow-md">
                  <Mail size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Email Support</h3>
                  <a
                    href="mailto:info@uctc.edu.bd"
                    className="mt-1 inline-block text-sm text-slate-500 transition hover:text-[#087f8c]"
                  >
                    info@uctc.edu.bd
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl border border-white bg-white/90 p-6 shadow-2xl shadow-slate-200/70 backdrop-blur-xl sm:p-8 md:p-10"
          >
            <div className="mb-8">
              <h3 className="text-2xl font-extrabold text-slate-900">
                Get in Touch
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Please provide your details and message below.
              </p>
            </div>

            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700"
              >
                <CheckCircle2 size={20} className="mt-0.5 shrink-0" />
                <span>{success}</span>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="mb-2 block text-sm font-bold text-slate-700"
                  >
                    Your Name <span className="text-red-500">*</span>
                  </label>

                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    autoComplete="name"
                    required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#087f8c] focus:bg-white focus:ring-4 focus:ring-cyan-100"
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-email"
                    className="mb-2 block text-sm font-bold text-slate-700"
                  >
                    Email Address <span className="text-red-500">*</span>
                  </label>

                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#087f8c] focus:bg-white focus:ring-4 focus:ring-cyan-100"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="contact-subject"
                  className="mb-2 block text-sm font-bold text-slate-700"
                >
                  Subject <span className="text-red-500">*</span>
                </label>

                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What is your message about?"
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#087f8c] focus:bg-white focus:ring-4 focus:ring-cyan-100"
                />
              </div>

              <div>
                <label
                  htmlFor="contact-message"
                  className="mb-2 block text-sm font-bold text-slate-700"
                >
                  Your Message <span className="text-red-500">*</span>
                </label>

                <textarea
                  id="contact-message"
                  rows={5}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Write your message here..."
                  required
                  className="w-full resize-y rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#087f8c] focus:bg-white focus:ring-4 focus:ring-cyan-100"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#075b75] to-[#087f8c] px-6 py-4 font-bold text-white shadow-lg shadow-cyan-900/20 transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 size={19} className="animate-spin" />
                    Sending Your Message...
                  </>
                ) : (
                  <>
                    <Send
                      size={18}
                      className="transition group-hover:translate-x-1"
                    />
                    Send Message
                  </>
                )}
              </button>

              <p className="text-center text-xs leading-5 text-slate-400">
                Your information will be used to respond to your inquiry.
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      {/* ================= MAP SECTION ================= */}
      <section className="px-6 pb-20 sm:px-8 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-7xl"
        >
          <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-700">
                <MapPin size={16} />
                Find Our Campus
              </div>

              <h2 className="mt-4 text-3xl font-black text-slate-900 sm:text-4xl">
                Visit Us
              </h2>

              <p className="mt-3 max-w-2xl leading-7 text-slate-500">
                We welcome you to visit the University of Creative Technology
                Chittagong.
              </p>
            </div>

            <a
              href="https://maps.google.com/?q=University+of+Creative+Technology+Chittagong"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-fit items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:border-cyan-200 hover:text-[#087f8c]"
            >
              Open in Google Maps
              <ArrowUpRight size={17} />
            </a>
          </div>

          <div className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-2 shadow-xl">
            <iframe
              title="University of Creative Technology Chittagong Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3689.599683417447!2d91.84537557563125!3d22.368739479637373!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30ad2775b77746a9%3A0xc707890ddb46a3b1!2sUniversity%20of%20Creative%20Technology%20Chittagong!5e0!3m2!1sen!2sbd!4v1759593280040!5m2!1sen!2sbd"
              className="h-[350px] w-full rounded-2xl border-0 sm:h-[450px]"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </motion.div>
      </section>
    </div>
  );
}